import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import Modal from './Modal';
import { showToast } from '../Utils/toast';
import api from '../Utils/api';
import { usePlausible } from '../hooks/usePlausible';

const AddToPlaylistModal = inject('PlaylistStore')(
  observer(({ PlaylistStore }) => {
    const { addingToPlaylistSong, addToPlaylistCurrentPlaylist, modalState, userPlaylists } = PlaylistStore;
    const { trackEvent } = usePlausible();

    // Local state
    const [playlistStates, setPlaylistStates] = useState({});
    // { [playlistId]: { contains: boolean, loading: boolean } }
    const [initializing, setInitializing] = useState(true);

    // Filter manageable playlists (exclude virtual, shared with view-only access)
    const manageablePlaylists = userPlaylists.filter(p =>
      !p.isVirtual &&
      (!p.isShared || p.accessLevel === 'manage')
    );

    // Initialize: Check which playlists contain the song
    useEffect(() => {
      if (!modalState.addToPlaylist || !addingToPlaylistSong) return;

      const checkPlaylists = async () => {
        setInitializing(true);
        const states = {};

        // Parallel check all playlists
        await Promise.all(
          manageablePlaylists.map(async (playlist) => {
            try {
              const { list } = await api.getSongList(playlist.id);
              const contains = list.some(song => song.mediaId === addingToPlaylistSong.mediaId);
              states[playlist.id] = { contains, loading: false };
            } catch (error) {
              console.error(`Failed to check playlist ${playlist.id}:`, error);
              states[playlist.id] = { contains: false, loading: false };
            }
          })
        );

        setPlaylistStates(states);
        setInitializing(false);
      };

      checkPlaylists();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalState.addToPlaylist, addingToPlaylistSong?.mediaId]);

    const handleToggle = async (playlistId, playlistTitle) => {
      const currentState = playlistStates[playlistId];
      const newContainsValue = !currentState.contains;

      // Optimistic update
      setPlaylistStates(prev => ({
        ...prev,
        [playlistId]: { contains: newContainsValue, loading: true }
      }));

      try {
        await showToast.promise(
          PlaylistStore.toggleSongInPlaylist(
            playlistId,
            addingToPlaylistSong.mediaId,
            currentState.contains
          ),
          {
            loading: currentState.contains ? 'Removing from playlist...' : 'Adding to playlist...',
            success: currentState.contains
              ? `Removed from "${playlistTitle}"`
              : `Added to "${playlistTitle}"`,
            error: currentState.contains
              ? `Failed to remove from "${playlistTitle}"`
              : `Failed to add to "${playlistTitle}"`,
          }
        );

        // Track successful action
        trackEvent(currentState.contains ? 'Remove Song from Playlist' : 'Add Song to Playlist', {
          playlistId,
          playlistTitle,
          mediaId: addingToPlaylistSong.mediaId,
          songTitle: addingToPlaylistSong.title,
        });

        // Success: keep optimistic state, stop loading
        setPlaylistStates(prev => ({
          ...prev,
          [playlistId]: { contains: newContainsValue, loading: false }
        }));
      } catch (error) {
        // Revert on error
        setPlaylistStates(prev => ({
          ...prev,
          [playlistId]: { contains: currentState.contains, loading: false }
        }));
      }
    };

    const handleClose = () => {
      PlaylistStore.closeAddToPlaylistModal();
      setPlaylistStates({});
      setInitializing(true);
    };

    if (!addingToPlaylistSong) return null;

    return (
      <Modal
        isOpen={modalState.addToPlaylist}
        onClose={handleClose}
        title="Add to Playlist"
        size="md"
      >
        <div className="space-y-4">
          {/* Song info */}
          <div className="pb-4 border-b border-secondary">
            <p className="text-sm text-text font-medium truncate">
              {addingToPlaylistSong.title}
            </p>
            <p className="text-xs text-gray-400 truncate">
              {addingToPlaylistSong.artist}
            </p>
          </div>

          {/* Loading state */}
          {initializing && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-accent" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {/* Playlist list */}
          {!initializing && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {manageablePlaylists.length === 0 && (
                <p className="text-sm text-center text-gray-400 py-8">
                  No playlists available. Create one first!
                </p>
              )}

              {manageablePlaylists.map((playlist) => {
                const state = playlistStates[playlist.id] || { contains: false, loading: false };
                const isCurrentPlaylist = playlist.id === addToPlaylistCurrentPlaylist;

                return (
                  <div
                    key={playlist.id}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg
                      ${isCurrentPlaylist ? 'bg-accent bg-opacity-10' : 'hover:bg-secondary'}
                      transition-colors
                    `}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={state.contains}
                      onChange={() => handleToggle(playlist.id, playlist.title)}
                      disabled={state.loading}
                      className="
                        w-5 h-5 rounded border-2 border-neutral-700 bg-transparent checked:bg-accent checked:border-accent focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors
                      "
                    />

                    {/* Playlist title */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text truncate">
                        {playlist.title}
                        {isCurrentPlaylist && (
                          <span className="ml-2 text-xs text-gray-400">
                            (current)
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Loading spinner */}
                    {state.loading && (
                      <svg className="animate-spin h-4 w-4 text-accent" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    );
  })
);

export default AddToPlaylistModal;
